import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Ticket, Lock, LogOut, ShieldAlert, ArrowLeft, Megaphone, CreditCard } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";

const LINKS = [
  { to: "/superadmin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/superadmin/organisations", label: "Organisations", icon: Building2 },
  { to: "/superadmin/paywall", label: "Subscriptions", icon: Lock },
  { to: "/superadmin/promos", label: "Promos & Offers", icon: Ticket },
  { to: "/superadmin/ticker", label: "Ticker", icon: Megaphone },
  { to: "/superadmin/payments", label: "Payments", icon: CreditCard },
];

export function SuperadminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-200">
      <aside className="hidden w-64 shrink-0 border-r border-white/10 md:block">
        <div className="fixed bottom-0 left-0 top-[var(--ticker-h,0px)] flex w-64 flex-col">
          <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
            <img src="/icon-192.png" alt="FHAPAS" className="h-9 w-9 rounded-lg bg-white p-0.5" />
            <div>
              <p className="font-display text-sm font-bold text-white">FHAPAS</p>
              <p className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-amber-400">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 pulse-dot" /> Developer Console
              </p>
            </div>
          </div>
          <nav className="flex-1 space-y-1 px-3 py-4">
            {LINKS.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} data-testid={`sa-nav-${l.label.toLowerCase().split(" ")[0]}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-teal-600 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
                <l.icon className="h-[18px] w-[18px]" /> {l.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-white/10 p-3">
            <button onClick={() => navigate("/app/dashboard")} className="mb-1 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-400 hover:bg-white/5 hover:text-white" data-testid="sa-back-app">
              <ArrowLeft className="h-4 w-4" /> Back to app
            </button>
            <button onClick={logout} data-testid="sa-logout" className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-rose-400 hover:bg-white/5">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-[var(--ticker-h,0px)] z-30 flex h-16 items-center gap-3 border-b border-white/10 glass-dark px-4 md:px-8">
          <div className="flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-semibold text-amber-400">
            <ShieldAlert className="h-4 w-4" /> Superadmin Mode
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-semibold text-white">{user?.full_name}</p>
              <p className="text-[11px] text-slate-400">{user?.email}</p>
            </div>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-teal-600 text-xs font-bold text-white">{initials(user?.full_name)}</span>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1500px] flex-1 px-4 py-6 md:px-8 animate-fade-up">{children}</main>
      </div>
    </div>
  );
}
